import { takeLatest, call, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { orderSchoolsApi } from 'services/apiDigitalTicket/ordersSchool';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import CONST_ORDERS from 'constants/orders';
import CONST_PAYMENT from 'constants/payments';
import PATH from 'constants/basePath';

import * as OrdersSchoolActions from './actions';
import { OrdersSchoolTypes } from './types';

function parseJsonToValidObject(str: string) {
  let obj;
  try {
    obj = JSON.parse(str);
  } catch (e) {
    return str;
  }
  return obj;
}

function* list({
  payload,
}: ReturnType<typeof OrdersSchoolActions.listOrdersSchoolRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando pedidos' }));
    const response = yield call(orderSchoolsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      const data = response.data.map((order: any) => {
        /* eslint-disable prefer-destructuring */
        let orderStatus = CONST_ORDERS.STATUS.find(
          (item) => item.KEY === order.status,
        );
        if (!orderStatus) {
          orderStatus = CONST_ORDERS.STATUS[0];
        }

        let paymentStatus = CONST_PAYMENT.STATUS.find(
          (item) => item.KEY === order?.PaymentRequest?.status,
        );
        if (!paymentStatus) {
          paymentStatus = CONST_PAYMENT.STATUS[0];
        }

        let paymentType = CONST_PAYMENT.TYPES.find(
          (item) => item.KEY === order?.PaymentRequest?.type,
        );
        if (!paymentType) {
          paymentType = { KEY: '', VALUE: '---' };
        }

        return {
          ...order,
          status: orderStatus.VALUE,
          paymentStatus: paymentStatus.VALUE,
          paymentType: paymentType.VALUE,
          PaymentRequest: {
            ...order?.PaymentRequest,
            PaymentRequestHistories: order?.PaymentRequest?.PaymentRequestHistories.map(
              (history: any) => ({
                ...history,
                message: parseJsonToValidObject(history.message),
              }),
            ),
          },
        };
      });
      yield put(
        OrdersSchoolActions.listOrdersSchoolSuccess({
          chargeRequestSchoolList: data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - listOrders - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersSchoolActions.listOrdersSchoolFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof OrdersSchoolActions.getOrderSchoolRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const response = yield call(orderSchoolsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        OrdersSchoolActions.getOrderSchoolSuccess({
          chargeRequestSchoolData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - getOrder - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersSchoolActions.getOrderSchoolFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof OrdersSchoolActions.createOrderSchoolRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando pedido' }));
    const response = yield call(orderSchoolsApi.create, { data: payload });

    if (response.status === 200) {
      yield put(
        OrdersSchoolActions.createOrderSchoolSuccess({
          chargeRequestSchoolData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido criado com sucesso',
        }),
      );
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - createOrder - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersSchoolActions.createOrderSchoolFailure());
    yield meta.promise.reject(error);
    yield put(DialogsActions.setClose());
  }
}

function* repeat({
  payload,
}: ReturnType<typeof OrdersSchoolActions.repeatOrderSchoolRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando pedido' }));
    const response = yield call(orderSchoolsApi.repeat, { data: payload });

    if (response.status === 200) {
      yield put(
        OrdersSchoolActions.repeatOrderSchoolSuccess({
          chargeRequestRepeatSchoolData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido criado com sucesso',
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido criado com sucesso',
        }),
      );

      yield put(
        OrdersSchoolActions.listOrdersSchoolRequest({
          _includes: 'payment,history,order',
          _order: 'DESC',
          _sort: 'createdAt',
          _limit: 9999,
        }),
      );

      yield put(push(PATH.TEACHING_INSTITUTION_ORDERS_HISTORY.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - repeatOrder - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DialogsActions.setClose());
  }
  yield put(OrdersSchoolActions.repeatOrderSchoolFailure());
}

function* filter({
  payload,
}: ReturnType<typeof OrdersSchoolActions.filterOrdersSchoolRequest>): any {
  try {
    const response = yield call(orderSchoolsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      const data = response.data.map((order: any) => {
        /* eslint-disable prefer-destructuring */
        let orderStatus = CONST_ORDERS.STATUS.find(
          (item) => item.KEY === order.status,
        );
        if (!orderStatus) {
          orderStatus = CONST_ORDERS.STATUS[0];
        }

        let paymentStatus = CONST_PAYMENT.STATUS.find(
          (item) => item.KEY === order?.PaymentRequest?.status,
        );
        if (!paymentStatus) {
          paymentStatus = CONST_PAYMENT.STATUS[0];
        }

        let paymentType = CONST_PAYMENT.TYPES.find(
          (item) => item.KEY === order?.PaymentRequest?.type,
        );
        if (!paymentType) {
          paymentType = { KEY: '', VALUE: '---' };
        }

        return {
          ...order,
          status: orderStatus.VALUE,
          paymentStatus: paymentStatus.VALUE,
          paymentType: paymentType.VALUE,
          PaymentRequest: {
            ...order?.PaymentRequest,
            PaymentRequestHistories: order?.PaymentRequest?.PaymentRequestHistories.map(
              (history: any) => ({
                ...history,
                message: parseJsonToValidObject(history.message),
              }),
            ),
          },
        };
      });

      yield put(
        OrdersSchoolActions.filterOrdersSchoolSuccess({
          chargeRequestSchoolList: Array.isArray(data) ? data : [data],
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - filterOrders - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(OrdersSchoolActions.filterOrdersSchoolFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeChargeRequest({
  payload,
}: ReturnType<
  typeof OrdersSchoolActions.deleteChargeRequestSchoolRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo pedido' }));
    const response = yield call(orderSchoolsApi.deleteChargeRequest, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido excluído com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(OrdersSchoolActions.deleteChargeRequestSchoolSuccess());
      yield put(
        OrdersSchoolActions.listOrdersSchoolRequest({
          _includes: 'payment,history,order',
          _order: 'DESC',
          _sort: 'createdAt',
          _limit: 10,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OrdersSchoolActions.deleteChargeRequestSchoolFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const ordersSchoolSagas = [
  takeLatest(OrdersSchoolTypes.CREATE_ORDER_SCHOOL_REQUEST, create),
  takeLatest(
    OrdersSchoolTypes.CHARGE_REQUEST_REPEAT_ORDER_SCHOOL_REQUEST,
    repeat,
  ),
  takeLatest(OrdersSchoolTypes.LIST_ORDERS_SCHOOL_REQUEST, list),
  takeLatest(OrdersSchoolTypes.GET_ORDER_SCHOOL_REQUEST, get),
  takeLatest(OrdersSchoolTypes.FILTER_ORDER_SCHOOL_REQUEST, filter),
  takeLatest(
    OrdersSchoolTypes.DELETE_CHARGE_REQUEST_SCHOOL_REQUEST,
    removeChargeRequest,
  ),
];
